import React from 'react';
import { Button } from './Button';
import Avatar from './Avatar';
import { ChatMessageRole } from '../../Types/Enums';

interface OptionalColProps {
    content: string | React.ReactNode;
    className?: string;
    showImage?: boolean;
    image?: string;
    onClick?: () => void;
    showImageMobile?: boolean;
    mobileSmall?: boolean;
    hideCol?: boolean;
}

interface AdminViewContentProps {
    identifier: any;
    mobile?: boolean;
    gridCols?: string;
    useSmallFontMobile?: boolean;
    cols?: {
        button?: {
            buttons?: {
                variant?: string;
                onClick?: () => void;
                icon?: React.ReactNode;
                tooltipText?: string;
                text?: string;
                className?: string;
                hideElement?: boolean;
                demo?: boolean;
            }[];
        };
        optionalCols?: OptionalColProps[];
        mobileOrder?: {
            main?: string;
            optionalCols?: string[];
        };
    };
}

export default function AdminViewContent({
    identifier,
    mobile = false,
    useSmallFontMobile = false,
    gridCols = '',
    cols,
}: AdminViewContentProps) {
    const mainCol = cols?.optionalCols?.[0];
    const restOptionalCols = cols?.optionalCols?.slice(1) || [];

    // Use the first primary (variant) button (index 0) as the action for row clicks.
    const firstPrimaryButton = cols?.button?.buttons?.find((button) => {
        return button.variant?.includes('primary');
    });

    const handleRowClick = (e?: React.MouseEvent) => {
        if (firstPrimaryButton?.onClick) {
            firstPrimaryButton.onClick();
            return;
        }
        mainCol?.onClick && mainCol.onClick();
    };

    return mobile ? (
        <div
            key={identifier}
            className={`flex flex-col border border-layout-borderColor p-4 my-2 rounded-lg`}
            onClick={handleRowClick}
        >
            <div className={`flex justify-between items-center ${cols?.optionalCols ? '' : ''}`}>
                <div className={`truncate w-full ${mainCol?.showImage && 'flex items-center gap-3'}`}>
                    {mainCol?.showImage && (
                        <div className="w-[30px] h-[30px] relative">
                            <Avatar
                                role={ChatMessageRole.assistant}
                                avatarImage={mainCol?.image || ''}
                                size={30}
                                title={typeof mainCol.content === 'string' ? mainCol.content : ''}
                            />
                        </div>
                    )}
                    {cols?.button?.buttons ? (
                        <strong>{cols.mobileOrder?.main ?? mainCol?.content}</strong>
                    ) : (
                        <span>{cols?.mobileOrder?.main ?? mainCol?.content}</span>
                    )}
                </div>
                <>
                    {cols?.button?.buttons ? (
                        <div className="flex gap-1">
                            {cols?.button?.buttons?.map(
                                (button: any, index: number) =>
                                    !button.hideElement && (
                                        <Button
                                            key={index}
                                            variant={button.demo ? 'disabledRound' : button.variant}
                                            onClick={(e: React.MouseEvent) => {
                                                e.stopPropagation();
                                                button.onClick && button.onClick();
                                            }}
                                            tooltipText={button.tooltipText}
                                            className={button.className}
                                            disabled={button.demo}
                                        >
                                            {button.icon}
                                        </Button>
                                    ),
                            )}
                        </div>
                    ) : (
                        <>
                            <div className="truncate w-full text-right">
                                <strong>{cols?.mobileOrder?.optionalCols?.[0]}</strong>
                            </div>
                        </>
                    )}
                </>
            </div>
            {restOptionalCols.map((optionalCol, index) => {
                if ((!cols?.button?.buttons && index === 0) || !optionalCol.content || optionalCol.hideCol) {
                    return null;
                }
                return (
                    <div key={index} className="truncate w-full mt-2">
                        <div className="flex">
                            {useSmallFontMobile ? (
                                <small className={`${optionalCol.image && 'pr-[5px]'}`}>
                                    {cols?.mobileOrder?.optionalCols?.[index + 1] ?? optionalCol.content}
                                </small>
                            ) : (
                                <span className={`${optionalCol.image && 'pr-[5px]'}`}>
                                    {cols?.mobileOrder?.optionalCols?.[index + 1] ?? optionalCol.content}
                                </span>
                            )}
                            {optionalCol.image && optionalCol.showImageMobile && (
                                <div className="relative h-[20px]">
                                    <Avatar
                                        role={ChatMessageRole.assistant}
                                        size={20}
                                        avatarImage={'/img/logos/entra.png'}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    ) : (
        <div
            key={identifier}
            className={`grid ${gridCols} cursor-pointer gap-4 py-2 px-4 rounded-lg hover:bg-chatlist-item-background-hover`}
            onClick={handleRowClick}
        >
            {!mainCol?.hideCol && (
                <div className={`w-full flex items-center text-ellipsis overflow-hidden`}>
                    {mainCol?.showImage && (
                        <div className="w-[55px] h-[45px] relative">
                            <Avatar
                                role={ChatMessageRole.assistant}
                                avatarImage={mainCol?.image || ''}
                                size={45}
                                title={typeof mainCol.content === 'string' ? mainCol.content : ''}
                            />
                        </div>
                    )}
                    <span className={`${mainCol?.showImage && 'max-w-[calc(100%-55px)] truncate'}`}>
                        {mainCol?.content}
                    </span>
                </div>
            )}
            {restOptionalCols.map((optionalCol, index) => {
                if (optionalCol.hideCol) {
                    return <></>;
                }

                return (
                    <div key={index} className={`w-full flex items-center truncate text-ellipsis overflow-hidden`}>
                        {optionalCol.image && (
                            <div className="relative h-[25px]">
                                <Avatar
                                    role={ChatMessageRole.assistant}
                                    size={25}
                                    avatarImage={'/img/logos/entra.png'}
                                />
                            </div>
                        )}
                        <span className={`${optionalCol.className}${optionalCol.image ? ' pl-[35px]' : ''}`}>
                            {optionalCol.content}
                        </span>
                    </div>
                );
            })}
            <div className={`flex gap-1 justify-end items-center`}>
                {cols?.button?.buttons?.map(
                    (button: any, index: number) =>
                        !button.hideElement && (
                            <Button
                                key={index}
                                variant={button.demo ? 'disabledRound' : button.variant}
                                onClick={(e: React.MouseEvent) => {
                                    e.stopPropagation();
                                    button.onClick && button.onClick();
                                }}
                                tooltipText={button.tooltipText}
                                className={button.className}
                                disabled={button.demo}
                            >
                                {button.icon}
                            </Button>
                        ),
                )}
            </div>
        </div>
    );
}
